import { FLAGS } from '@iheartjane/feature-flags';
import { getRootDomain } from '@iheartjane/utils';
import { isbot } from 'isbot';
import compact from 'lodash-es/compact';
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
  MiddlewareFunction,
} from 'react-router';
import { data, useLoaderData } from 'react-router';
import { getToast } from 'remix-toast';

import { updateShoppingPreferences } from '@jane/bloom/actions/store.server';
import { Document } from '@jane/bloom/components/document/document';
import { tagManagerScriptSrc } from '@jane/bloom/components/document/googleTagPlatform';
import { useFallbackStoreResolution } from '@jane/bloom/hooks/storeSwitcher/useFallbackStoreResolution';
import { useTrackInitialStorePin } from '@jane/bloom/hooks/useTrackInitialStorePin';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import {
  JANE_GOLD_CONFIG_CONTEXT,
  provideJaneGoldConfig,
} from '@jane/bloom/middlewares/janeGoldConfigContext.server';
import {
  JDID_CONTEXT,
  provideJdid,
} from '@jane/bloom/middlewares/jdidContext.server';
import {
  MENU_CONTEXT,
  provideBloomMenu,
} from '@jane/bloom/middlewares/menuContext.server';
import { faviconTag } from '@jane/bloom/resources/meta/favicon';
import { fontCssTags } from '@jane/bloom/resources/meta/fontCss';
import { themeColor } from '@jane/bloom/resources/meta/themeColor';
import { env } from '@jane/bloom/services/env.server';
import type { AgeGateResult } from '@jane/bloom/sessions/ageGate.server';
import { getAgeGate } from '@jane/bloom/sessions/ageGate.server';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import {
  getPromoCode,
  setPromoCode,
} from '@jane/bloom/sessions/promoCode.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import type { LocalRootLoaderData } from '@jane/bloom/types/loaders/rootLoader';
import type { RouteHandle } from '@jane/bloom/types/routeHandle';
import { adoptEmbeddedCart } from '@jane/bloom/utils/cart/adoptEmbeddedCart.server';
import { getDefaultCart } from '@jane/bloom/utils/cart/getDefaultCart.server';
import { stateNameToAbbreviation } from '@jane/bloom/utils/geo/stateNameToAbbreviation';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import { promiseHashAllSettled } from '@jane/bloom/utils/requests/promiseHashAllSettled';
import { buildCategoryImages } from '@jane/bloom/viewModels/categoryImages';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';

import defaultFaviconSrc from './assets/favicon.ico';

import '@iheartjane/reefer/styles/index.css';
import stylesLink from './assets/styles.css?url';
import desktopStylesLink from './assets/styles.desktop.css?url';

export const middleware: MiddlewareFunction<Response>[] = [
  provideBloomMenu,
  provideJdid,
  provideJaneGoldConfig,
];

export const handle: RouteHandle<LocalRootLoaderData> = {
  scripts: ({
    data: {
      appConfig: { headlessEmbedLink },
      appleSsoClientId,
      googleSsoClientId,
      googleTagPlatform: { gtmContainerId },
    },
  }) => {
    return compact([
      /** Google Tag Manager - create the script tag directly to ensure it is loaded, ignoring possible React rehydration. */
      gtmContainerId && {
        async: true,
        defer: true,
        src: tagManagerScriptSrc(gtmContainerId),
      },
      /** @see https://docs.iheartjane.com/jane-docs/implementing-roots/embedding-checkout */
      headlessEmbedLink && {
        async: true,
        defer: true,
        id: 'jane-frame-script',
        src: headlessEmbedLink,
      },
      /**
       * Sign In With Google client — needed app-wide while the auth modal
       * (which can open on any route) is enabled.
       * @see https://developers.google.com/identity/gsi/web/guides/client-library
       */
      googleSsoClientId && {
        async: true,
        defer: true,
        id: 'google-gsi-client',
        src: 'https://accounts.google.com/gsi/client',
      },
      /**
       * Sign In With Apple JS client — needed app-wide while the auth modal
       * (which can open on any route) is enabled.
       * @see https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
       */
      appleSsoClientId && {
        async: true,
        defer: true,
        id: 'apple-signin-client',
        src: 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      },
    ]);
  },
};

export const links: LinksFunction = () => [
  {
    crossOrigin: 'anonymous',
    href: 'https://fonts.googleapis.com',
    rel: 'preconnect',
  },
  {
    crossOrigin: 'anonymous',
    href: 'https://fonts.gstatic.com',
    rel: 'preconnect',
  },
  { href: stylesLink, rel: 'stylesheet' },
  { href: desktopStylesLink, media: '(min-width: 768px)', rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  if (!loaderData) return [];

  const faviconSrc = loaderData.faviconUrl ?? defaultFaviconSrc;

  return compact([
    faviconTag(faviconSrc),
    { content: loaderData.buildVersion, name: 'version' },
    {
      href: `${loaderData.menuUrl}/manifest.json`,
      rel: 'manifest',
      tagName: 'link',
    },
    themeColor(loaderData.theme.bloom?.colors?.header?.main),
    ...fontCssTags(loaderData.theme.bloom?.fonts),
  ]);
};

export async function loader({ context, request, url }: LoaderFunctionArgs) {
  const cookie = request.headers.get('Cookie');
  const { activeMenuUrl, menu } = context.get(MENU_CONTEXT);
  const { goldConfig } = context.get(JANE_GOLD_CONFIG_CONTEXT);
  const {
    apple_sso_client_id,
    category_images,
    cookie_prefix,
    custom_seo,
    favicon_url,
    footer,
    google_sso_client_id,
    google_tag_manager_container_id,
    link_to_main_site,
    logo,
    menu_url,
    operator: { id: operatorId, name: operatorName },
    partner_scripts,
    store_slugs,
    stores,
    theme,
  } = menu;
  const { pinnedStore, shoppingPreferences, wasStoreInitiallyResolved } =
    await globalSubloader(request, menu);

  const isBot = isbot(request.headers.get('user-agent'));
  const promoCodeParam = url.searchParams.get('promo_code');

  const {
    ageGate,
    cart,
    promoCode,
    remixToast: { headers, toast },
    userIdentity,
  } = await promiseHashAllSettled({
    ageGate: {
      defaultValue: {
        confirmedAge: false,
        type: 'yes_no' as const,
      } satisfies AgeGateResult,
      /**
       * @todo Age gate cookie checks are now only a client-side concern.
       * Consider performing this check without the server request, possibly with a root clientLoader.
       */
      promise: getAgeGate(request, theme, cookie_prefix, pinnedStore.id),
    },
    cart: {
      defaultValue: getDefaultCart(),
      promise: getCartInfo(cookie, cookie_prefix),
    },
    promoCode: {
      defaultValue: undefined,
      promise: getPromoCode(cookie, cookie_prefix),
    },
    remixToast: {
      defaultValue: { headers: new Headers(), toast: undefined },
      promise: getToast(request),
    },
    userIdentity: {
      defaultValue: null,
      promise: getUserIdentity(request, cookie_prefix),
    },
  });

  // If promo_code is in URL params, set it in the session
  if (promoCodeParam) {
    const promoCodeCookie = await setPromoCode(cookie_prefix, promoCodeParam);
    headers.append('Set-Cookie', promoCodeCookie);
  }

  const shoppingPreferencesHeaders = await updateShoppingPreferences({
    args: shoppingPreferences,
    cookie,
    cookiePrefix: cookie_prefix,
    // Skip verification when the store was just resolved server-side —
    // it was already validated against menu.stores moments ago.
    verifyStoreId: !wasStoreInitiallyResolved,
  });
  if (shoppingPreferencesHeaders) {
    shoppingPreferencesHeaders.forEach((value, key) => {
      headers.append(key, value);
    });
  }

  // The JDID is resolved once per request by the provideJdid middleware.
  const jdid = context.get(JDID_CONTEXT);

  let reservationMode = '';
  if (pinnedStore.pickup && pinnedStore.delivery) {
    reservationMode = `${
      getCustomLanguage(pinnedStore).reservationModeLabels.pickup || 'pickup'
    } and ${
      getCustomLanguage(pinnedStore).reservationModeLabels.delivery ||
      'delivery'
    }`;
  } else if (pinnedStore.pickup) {
    reservationMode =
      getCustomLanguage(pinnedStore).reservationModeLabels.pickup || 'pickup';
  } else if (pinnedStore.delivery) {
    reservationMode =
      getCustomLanguage(pinnedStore).reservationModeLabels.delivery ||
      'delivery';
  }

  const fulfillmentMode = pinnedStore.recreational
    ? pinnedStore.store_compliance_language_settings?.['recreational'] ||
      'recreational'
    : 'medical';

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [
      FLAGS.c3MidGridAdRow,
      FLAGS.obGtagEvents,
      FLAGS.c3SpecialsRedesignMenu,
      FLAGS.c1BundleTag,
      FLAGS.c1CompareProducts,
      FLAGS.c1ExtendedDealsLabels,
      FLAGS.obWizardBannerEnabled,
      FLAGS.c3ThematicRowsContext,
      FLAGS.c3UseV3StoreSpecials,
      FLAGS.c2EmbeddedRowAddToBag,
      FLAGS.c2BloomMixpanelReidentify,
      FLAGS.c2NoEmptyPosts,
      FLAGS.c2CategoryOnPageSeo,
      FLAGS.c2CustomCategoryImages,
      FLAGS.c2CustomComponents,
      FLAGS.c2CustomComponentsQuantity,
      FLAGS.c3CarouselImageFix,
      FLAGS.c3ShowRelievedFeelingLabel,
      FLAGS.p1PlaceImageAdSecond,
    ],
    jdid,
    request,
  });

  const { finalCart, finalPinnedStore } = await adoptEmbeddedCart({
    cart,
    cookie,
    cookiePrefix: cookie_prefix,
    domain: getRootDomain(new URL(activeMenuUrl)),
    enabled: featureFlags[FLAGS.c2EmbeddedRowAddToBag],
    headers,
    pinnedStore,
    shoppingPreferences,
    stores: menu.stores,
  });

  const rootData: LocalRootLoaderData = {
    ageGate,
    appConfig: {
      aeropay: {
        consumerId: env.aeropay.consumerId,
        env: env.aeropay.env,
      },
      datadog: {
        appId: env.datadog.appId,
        debug: env.datadog.debug,
        env: env.datadog.env,
        token: env.datadog.clientToken,
      },
      facebookAppId: env.facebookAppId,
      googlePlacesApiKey: env.googlePlacesApiKey,
      headlessEmbedLink: env.headlessEmbedLink,
      mixpanelProjectToken: env.mixpanelProjectToken,
      moengage: {
        env: env.moengage.env,
        workspaceId: env.moengage.workspaceId,
      },
    },
    appleSsoClientId: apple_sso_client_id,
    buildVersion: env.gitCommitSha,
    canonicalMenuUrl: menu_url,
    cart: finalCart,
    categoryImages: buildCategoryImages(category_images),
    cookiePrefix: cookie_prefix,
    customLanguage: getCustomLanguage(finalPinnedStore),
    customSEO: custom_seo,
    faviconUrl: favicon_url,
    featureFlags,
    goldConfig,
    googleSsoClientId: google_sso_client_id,
    googleTagPlatform: {
      gtmContainerId: google_tag_manager_container_id,
    },
    isBot,
    jdid,
    linkToMainSite: link_to_main_site,
    logo,
    menuFooter: footer,
    menuUrl: activeMenuUrl,
    metaProps: {
      fulfillmentMode,
      pinnedStoreCity: finalPinnedStore.city,
      pinnedStoreName: finalPinnedStore.name || operatorName,
      pinnedStoreState: stateNameToAbbreviation(finalPinnedStore.state),
      reservationMode,
    },
    nodeEnv: env.nodeEnv,
    operator: {
      id: operatorId,
      name: operatorName,
    },
    partnerScripts: partner_scripts ?? [],
    pinnedStore: finalPinnedStore,
    pluginVersion: request.headers.get('x-jane-plugin-version') ?? undefined,
    promoCode: promoCodeParam ?? promoCode,
    publicPath: env.publicUrl,
    shoppingPreferences,
    stores,
    storeSlugs: store_slugs,
    theme,
    toast,
    wasStoreInitiallyResolved,
    whoami: {
      user: userIdentity?.user || {},
      user_segments: userIdentity?.user_segments || [],
    },
  };

  return data(rootData, { headers });
}

export default function App() {
  const rootData = useLoaderData<LocalRootLoaderData>();
  useFallbackStoreResolution();
  useTrackInitialStorePin();
  return <Document {...rootData} />;
}
